import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';

export interface EthernetParameter {
  group: string;
  name: string;
  index: number;
  subindex: number;
  bitLength: number;
  dataType: number;
  code: number;
  flags: number;
  data: number[];
}

export interface EthernetMappedParameterValue {
  index: number;
  subindex: number;
  value: number | string | Uint8Array;
}

export interface EthernetDefaultResponse {
  message?: string;
  success: boolean;
}

export interface EthernetConnectResponse {
  connected: boolean;
}

export interface EthernetDisconnectResponse {
  disconnected: boolean;
}

export interface EthernetGetStateResponse extends EthernetDefaultResponse {
  state: number;
}

export interface EthernetReadSdoResponse extends EthernetDefaultResponse {
  data: number[];
  index: number;
  subindex: number;
}

export interface EthernetWriteSdoResponse extends EthernetDefaultResponse {
  index: number;
  subindex: number;
}

export interface EthernetReadFileResponse extends EthernetDefaultResponse {
  content: number[];
  size: number;
}

export interface EthernetWriteFileResponse extends EthernetDefaultResponse {
  size: number;
}

export interface EthernetGetParametersResponse extends EthernetDefaultResponse {
  parameters: EthernetParameter[];
}

export interface EthernetLoadParametersResponse extends EthernetDefaultResponse {
  parameterCount: number;
}

export interface EthernetGetParameterResponse extends EthernetDefaultResponse {
  parameter: EthernetParameter;
}

export interface EthernetGetMappedParameterValuesResponse extends EthernetDefaultResponse {
  entries: {
    rx: EthernetMappedParameterValue[];
    tx: EthernetMappedParameterValue[];
  };
}

@Injectable({
  providedIn: 'root',
})
export class EthernetService {
  baseUrl = 'http://localhost:9005';

  constructor(private http: HttpClient) {}

  connect(ip: string, port: number) {
    const url = `${this.baseUrl}/connect/${ip}/${port}`;

    return this.http.get<EthernetConnectResponse>(url);
  }

  disconnect() {
    const url = `${this.baseUrl}/disconnect`;

    return this.http.get<EthernetDisconnectResponse>(url);
  }

  getState() {
    const url = `${this.baseUrl}/state`;

    return this.http.get<EthernetGetStateResponse>(url);
  }

  setState(state: number) {
    return this.http.get<EthernetGetStateResponse>(`${this.baseUrl}/state/${state}`);
  }

  readSdo(index: number, subindex: number) {
    const url = `${this.baseUrl}/sdo/${index}/${subindex}`;

    return this.http.get<EthernetReadSdoResponse>(url);
  }

  writeSdo(index: number, subindex: number, value: number) {
    const url = `${this.baseUrl}/sdo/${index}/${subindex}/${value}`;

    return this.http.get<EthernetWriteSdoResponse>(url);
  }

  readFile(filename: string) {
    const url = `${this.baseUrl}/files/${filename}`;

    return this.http.get<EthernetReadFileResponse>(url);
  }

  writeFile(filename: string, body: ArrayBuffer) {
    const url = `${this.baseUrl}/files/${filename}`;
    const headers = {
      'Content-Type': 'application/octet-stream',
    };

    return this.http.post<EthernetWriteFileResponse>(url, body, { headers });
  }

  updateFirmware(body: ArrayBuffer, skipFiles: string[] = []) {
    const url = `${this.baseUrl}/update-firmware`;
    const headers = {
      'Content-Type': 'application/octet-stream',
    };

    return this.http.post<EthernetDefaultResponse>(url, body, {
      headers,
      params: new HttpParams().set('skip-files', skipFiles.join(',')),
    });
  }

  getParameters(readValues: boolean) {
    const url = `${this.baseUrl}/parameters`;
    const params = new HttpParams().set('readValues', String(readValues));

    return this.http.get<EthernetGetParametersResponse>(url, { params });
  }

  loadParameters(readValues: boolean) {
    const url = `${this.baseUrl}/load-parameters`;
    const params = new HttpParams().set('readValues', String(readValues));

    return this.http.get<EthernetLoadParametersResponse>(url, { params });
  }

  clearParameters() {
    const url = `${this.baseUrl}/clear-parameters`;

    return this.http.get<EthernetDefaultResponse>(url);
  }

  getParameter(index: number, subindex: number, readValues: boolean = true) {
    const url = `${this.baseUrl}/parameters/${index}/${subindex}`;
    const params = new HttpParams().set('readValues', String(readValues));

    return this.http.get<EthernetGetParameterResponse>(url, { params });
  }

  setParameter(index: number, subindex: number, value: number) {
    const url = `${this.baseUrl}/parameters/${index}/${subindex}/${value}`;

    return this.http.get<EthernetDefaultResponse>(url);
  }

  exchangeProcessDataAndUpdateParameters() {
    const url = `${this.baseUrl}/exchange-process-data-and-update-parameters`;

    return this.http.get<EthernetDefaultResponse>(url);
  }

  getMappedParameterValues() {
    const url = `${this.baseUrl}/mapped-parameter-values`;

    return this.http.get<EthernetGetMappedParameterValuesResponse>(url);
  }
}
